import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { usePatchMe } from '../api/hooks';
import { isPageTranslated } from '../lib/translationGuard';
import { pathInfo } from '../features/site/paths';
import { useSession } from './session';

const DISMISSED = 'btw.translationNotice.dismissed';

function prefersChinese(): boolean {
  const langs = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];
  return langs.some((l) => /^zh\b/i.test(l ?? ''));
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED) === '1';
  } catch {
    return false;
  }
}

/** Whether the browser is translating this page right now (Chrome toggles a class on <html>). */
function useBrowserTranslation(): boolean {
  const [on, setOn] = useState(() => typeof document !== 'undefined' && isPageTranslated());
  useEffect(() => {
    const obs = new MutationObserver(() => setOn(isPageTranslated()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return on;
}

/**
 * The app has its own Chinese interface. When a Chinese-speaking visitor lets the browser machine-translate a Dutch or
 * English app page (2026-10-28: J saw clumsy wording and a crash), offer the real translation in one click.
 * The text is fixed Chinese and marked translate="no", so the browser leaves it alone. Not on the public website,
 * which exists in Dutch and English only.
 */
export function TranslationNotice() {
  const translated = useBrowserTranslation();
  const { pathname } = useLocation();
  const { user, locale, setGuestLocale } = useSession();
  const patch = usePatchMe();
  const [dismissed, setDismissed] = useState(readDismissed);
  if (!translated || dismissed || locale === 'zh' || !prefersChinese() || pathInfo(pathname).lang !== null) return null;

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED, '1');
    } catch {
      /* ignore */
    }
  };
  const toChinese = () => {
    if (user) patch.mutate({ locale: 'zh' });
    else setGuestLocale('zh');
  };
  return (
    <div className="translation-notice" role="note" translate="no" lang="zh-CN" data-testid="translation-notice">
      <span>BTW Kwartier 有中文界面，比浏览器的自动翻译更准确。切换后，可以关闭浏览器的翻译。</span>
      <button type="button" className="btn btn--primary btn--sm" onClick={toChinese} disabled={patch.isPending}>
        切换到中文
      </button>
      <button type="button" className="translation-notice__close" aria-label="关闭" onClick={close}>
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
